import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomSheet } from '@shared/ui/adaptive/BottomSheet';
import { useMobileShell } from '@shared/shell';
import { allSuites } from '@suites/registry';
import { useActiveSuite } from '../useActiveSuite';
import { useAuth } from '@shared/auth/AuthContext';
import { useRecentPages } from '@shared/activity';
import { useTheme } from '@shared/theme/ThemeContext';
import { SuiteIcon } from '@shared/icons';
import { VIEW_OPTIONS } from '../topbar/RoleViewSwitcher';
import { Sun, Moon, LogOut, Check, ChevronRight } from 'lucide-react';
import { Separator } from '@shared/ui/separator';

export const MobileLauncherSheet: React.FC = () => {
  const { launcherOpen, closeLauncher } = useMobileShell();
  const navigate = useNavigate();
  const location = useLocation();
  const activeSuite = useActiveSuite();
  const { user, tenant, role, setRole, logout, hasPermission, enabledSuites, hasSuite } = useAuth();
  const recentPages = useRecentPages();
  const { theme, toggleTheme } = useTheme();

  const handleNavigate = (path: string) => {
    closeLauncher();
    if (path === '/webbuilder' || path === '/webbuilder/pages' || path.startsWith('/webbuilder/pages')) {
      window.open('/webbuilder/pages', '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
  };

  const permittedSuites = allSuites.filter((s) => {
    if (s.id === 'core') return true;
    if (enabledSuites && enabledSuites.length > 0 && !enabledSuites.includes(s.id)) return false;
    if (s.allowedRoles && s.allowedRoles.length > 0 && !s.allowedRoles.includes(role)) return false;
    return s.requiredPermission ? hasPermission(s.requiredPermission) : true;
  });

  return (
    <BottomSheet
      open={launcherOpen}
      onOpenChange={(open) => {
        if (!open) closeLauncher();
      }}
      title="Platform Navigation"
      description={tenant?.name ? `${tenant.name} · ${user?.email || ''}` : user?.email}
      className="max-h-[88dvh]"
    >
      <div className="space-y-6 pb-6">
        {/* Permitted Suites Grid */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">
            Suites & Modules
          </h3>
          <div className="grid grid-cols-4 gap-2.5">
            {permittedSuites.map((s) => {
              const isActive = activeSuite?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleNavigate(s.basePath)}
                  className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all ${
                    isActive
                      ? 'bg-primary/10 border-primary text-primary font-semibold shadow-sm'
                      : 'bg-card border-border/70 text-foreground hover:bg-accent/60'
                  }`}
                >
                  <SuiteIcon
                    name={s.icon}
                    className={`h-5 w-5 mb-1.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                  <span className="text-[11px] leading-tight line-clamp-1 w-full truncate">
                    {s.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Suite Pages */}
        {activeSuite && (
          <div>
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {activeSuite.name} Pages
              </h3>
              <span className="text-[10px] text-muted-foreground font-mono">
                {activeSuite.id}
              </span>
            </div>
            <div className="grid grid-cols-1 divide-y divide-border/40 rounded-xl border border-border/70 overflow-hidden bg-card">
              {activeSuite.nav
                .filter((item) => {
                  if (item.allowedRoles && item.allowedRoles.length > 0 && !item.allowedRoles.includes(role)) {
                    return false;
                  }
                  if (item.permission && !hasPermission(item.permission)) {
                    return false;
                  }
                  return true;
                })
                .map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      onClick={() => handleNavigate(item.path)}
                      className={`flex items-center justify-between px-3.5 py-3 text-left transition-colors ${
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground hover:bg-accent/50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <SuiteIcon
                          name={item.icon}
                          className={`h-4 w-4 shrink-0 ${
                            isActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                        />
                        <span className="text-xs truncate">{item.label}</span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* Role View Switcher */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 px-1">
            Switch Perspective
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {VIEW_OPTIONS.map((opt) => {
              const isSelected = role === opt.id;
              const Icon = opt.icon;
              const targetPath =
                opt.id === 'student'
                  ? hasSuite('sis')
                    ? '/sis/dashboard'
                    : '/core/dashboard'
                  : opt.id === 'teacher'
                  ? hasSuite('hr')
                    ? '/hr/dashboard'
                    : '/core/dashboard'
                  : hasSuite('bi')
                  ? '/bi/executive'
                  : '/core/dashboard';
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    setRole(opt.id);
                    handleNavigate(targetPath);
                  }}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-primary/10 border-primary text-foreground font-medium'
                      : 'bg-card border-border/60 text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={`h-4 w-4 ${opt.colorClass}`} />
                    <div className="text-left">
                      <div className="text-xs font-medium text-foreground">{opt.name}</div>
                      <div className="text-[10px] text-muted-foreground">{opt.badge}</div>
                    </div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent Pages */}
        {recentPages.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
              Recently Visited
            </h3>
            <div className="grid grid-cols-1 divide-y divide-border/40 rounded-xl border border-border/70 overflow-hidden bg-card">
              {recentPages.slice(0, 4).map((page) => (
                <button
                  key={page.key}
                  type="button"
                  onClick={() => handleNavigate(page.path)}
                  className="flex items-center justify-between px-3.5 py-2.5 text-left text-foreground hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <SuiteIcon name={page.icon} className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-xs truncate">{page.title}</span>
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                    {page.path}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <Separator />

        {/* Quick Settings & Logout */}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border/70 bg-card text-xs text-foreground hover:bg-accent transition-colors"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-sky-400" />}
            <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          <button
            type="button"
            onClick={async () => {
              closeLauncher();
              await logout();
              navigate('/login');
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-destructive/30 bg-destructive/10 text-xs text-destructive hover:bg-destructive/20 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default MobileLauncherSheet;
